import React, { useState, useEffect } from 'react';
import { useTheme } from '../theme/ThemeContext';

function formatValue(val, col, row) {
  if (col.render) return col.render(val, row);
  if (val == null || val === '') return '—';
  if (typeof val === 'number') {
    if (col.fmt === 'money') return Math.round(val).toLocaleString('ru-RU') + ' ₽';
    if (col.fmt === 'pct') {
      const n = typeof val === 'number' ? val : parseFloat(val);
      if (Number.isNaN(n)) return '—';
      const formatted = n * 100 < 10000
        ? n.toFixed(1)
        : n.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
      return `${formatted}%`;
    }
    return val.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  }
  if (typeof val === 'boolean') return val ? '✓' : '✗';
  return String(val);
}

export default function DataTable({ columns, rows, highlightRows = [], onRowClick, activeRowKey, rowKeyField, title, pageSize }) {
  const { theme } = useTheme();
  const hlSet = new Set(highlightRows);
  const clickable = typeof onRowClick === 'function';
  const allRows = rows || [];
  const paginated = pageSize && pageSize > 0;
  const totalPages = paginated ? Math.max(1, Math.ceil(allRows.length / pageSize)) : 1;

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows]);

  const visibleRows = paginated
    ? allRows.slice((page - 1) * pageSize, page * pageSize)
    : allRows;

  const goTo = (p) => setPage(Math.max(1, Math.min(totalPages, p)));

  const pageButtons = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  };

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: 'var(--card-rest)',
    }}>
      {title && (
        <div style={{
          padding: '14px 18px 0',
          color: 'var(--text-secondary)',
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 1,
          fontFamily: theme.fontDisplay,
        }}>
          {title}
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={i}
                  style={{
                    padding: '12px 16px',
                    color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border)',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    textAlign: i === 0 ? 'left' : 'right',
                    background: theme.mode === 'light' ? 'rgba(244,247,251,.9)' : 'rgba(12,18,32,.5)',
                  }}
                >
                  {col.label || col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, ri) => {
              const globalRi = paginated ? (page - 1) * pageSize + ri : ri;
              const rowKey = rowKeyField ? row[rowKeyField] : globalRi;
              const isActive = activeRowKey != null && rowKey === activeRowKey;
              const zebra = globalRi % 2 === 1;
              return (
                <tr
                  key={globalRi}
                  style={{
                    background: isActive
                      ? 'var(--accent-dim)'
                      : hlSet.has(globalRi)
                        ? 'var(--accent-dim)'
                        : zebra
                          ? (theme.mode === 'light' ? 'rgba(244,247,251,.65)' : 'rgba(255,255,255,.02)')
                          : 'transparent',
                    boxShadow: isActive ? 'inset 3px 0 0 var(--accent)' : undefined,
                    cursor: clickable ? 'pointer' : undefined,
                    transition: 'background .18s ease',
                  }}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  onMouseEnter={clickable ? (e) => {
                    if (!isActive) e.currentTarget.style.background = 'var(--accent-dim)';
                  } : undefined}
                  onMouseLeave={clickable ? (e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = zebra
                        ? (theme.mode === 'light' ? 'rgba(244,247,251,.65)' : 'rgba(255,255,255,.02)')
                        : 'transparent';
                    }
                  } : undefined}
                >
                  {columns.map((col, ci) => {
                    const key = col.key || col;
                    const val = Array.isArray(row) ? row[ci] : row[key];
                    return (
                      <td
                        key={ci}
                        className={ci > 0 ? 'tabular-nums' : undefined}
                        style={{
                          padding: '11px 16px',
                          color: ci === 0 ? 'var(--text-primary)' : 'var(--text-primary)',
                          fontWeight: ci === 0 ? 600 : 400,
                          borderBottom: '1px solid var(--border)',
                          whiteSpace: 'nowrap',
                          textAlign: ci === 0 ? 'left' : 'right',
                          fontVariantNumeric: ci > 0 ? 'tabular-nums' : 'normal',
                        }}
                      >
                        {formatValue(val, col, row)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {allRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="empty-state"
                  style={{ border: 'none', margin: 12, display: 'block' }}
                >
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {paginated && totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
          padding: '10px 16px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-card)',
        }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }} className="tabular-nums">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, allRows.length)} из {allRows.length.toLocaleString('ru-RU')}
          </span>
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <button type="button" style={pageBtn(theme)} disabled={page === 1} onClick={() => goTo(1)} title="Первая">«</button>
            <button type="button" style={pageBtn(theme)} disabled={page === 1} onClick={() => goTo(page - 1)} title="Предыдущая">‹</button>
            {pageButtons().reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) {
                acc.push(<span key={`ellipsis-${p}`} style={{ color: 'var(--text-muted)', padding: '0 4px' }}>…</span>);
              }
              acc.push(
                <button
                  type="button"
                  key={p}
                  style={{
                    ...pageBtn(theme),
                    ...(p === page ? {
                      background: 'var(--accent-dim)',
                      borderColor: 'var(--accent)',
                      color: 'var(--accent)',
                      fontWeight: 700,
                    } : {}),
                  }}
                  onClick={() => goTo(p)}
                >
                  {p}
                </button>,
              );
              return acc;
            }, [])}
            <button type="button" style={pageBtn(theme)} disabled={page === totalPages} onClick={() => goTo(page + 1)} title="Следующая">›</button>
            <button type="button" style={pageBtn(theme)} disabled={page === totalPages} onClick={() => goTo(totalPages)} title="Последняя">»</button>
          </div>
        </div>
      )}
    </div>
  );
}

function pageBtn(theme) {
  return {
    background: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 7,
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 500,
    padding: '4px 10px',
    cursor: 'pointer',
    minWidth: 32,
    transition: 'background .15s, color .15s',
  };
}
